import type { Language } from '@seek/melways-sites';
import { set } from 'mobx';
import { flow, getRoot, type Instance, types } from 'mobx-state-tree';

import {
  ApplicationSort,
  LocationDisplayFormat,
  SortOrder,
} from '../../App/AppPageBlock/graphql/hirer-types.generated';
import { fetchApplications } from '../../graphql/fetchApplications.hirer.graphql';
import { logError } from '../../utils/logError';
import type { IStore } from '../Store';
import { STATE_ERROR, STATE_LOADED, STATE_LOADING, stateType } from '../state';

import { BUCKETS } from './../JobApplicationStore/types';
import BucketStats from './BucketStats';

export interface BucketInfo {
  count: number;
  newCount?: number;
  type: BUCKETS;
}

const BucketStatsStore = types
  .model('BucketStatsStore', {
    bucketStats: types.optional(BucketStats, {}),
    state: stateType,
  })
  .views((self) => ({
    get isError() {
      return self.state === STATE_ERROR;
    },
    get isLoading() {
      return self.state === STATE_LOADING;
    },
    get bucketInfo(): BucketInfo[] {
      return [
        {
          count: self.bucketStats?.inboxCount ?? 0,
          newCount: self.bucketStats?.newCount ?? 0,
          type: BUCKETS.INBOX,
        },
        {
          count: self.bucketStats?.prescreenCount ?? 0,
          type: BUCKETS.PRESCREEN,
        },
        {
          count: self.bucketStats?.shortlistCount ?? 0,
          type: BUCKETS.SHORTLIST,
        },
        {
          count: self.bucketStats?.interviewCount ?? 0,
          type: BUCKETS.INTERVIEW,
        },
        {
          count: self.bucketStats?.offerCount ?? 0,
          type: BUCKETS.OFFER,
        },
        {
          count: self.bucketStats?.acceptCount ?? 0,
          type: BUCKETS.ACCEPT,
        },
        {
          count: self.bucketStats?.notSuitableCount ?? 0,
          type: BUCKETS.NOT_SUITABLE,
        },
      ];
    },
  }))
  .actions(() => ({
    runInAction(fn: (args?: any[]) => any) {
      return fn();
    },
  }))
  .actions((self) => {
    const loadBucketStats = flow(function* (
      jobId: number,
      language: Language,
      displayCountry: string,
    ) {
      const rootStore = getRoot(self) as IStore;
      try {
        const fetchApplicationsResult: Awaited<
          ReturnType<typeof fetchApplications>
        > = yield fetchApplications({
          input: {
            jobId,
            pagination: {
              pageSize: 0,
            },
            sort: {
              sortField: ApplicationSort.SCORE,
              orderBy: SortOrder.ASC,
            },
          },
          nationalitiesInput2: {
            jobId,
          },
          displayLabelInput2: {
            language,
          },
          countryNameInput2: {
            language,
          },
          displayDescriptionInput2: {
            language,
            displayFormat: LocationDisplayFormat.SHORT,
            displayCountry,
          },
        });

        self.runInAction(() => {
          set(self, {
            bucketStats: fetchApplicationsResult.statusFolderCounts,
            state: STATE_LOADED,
          });
        });
      } catch (error: any) {
        self.runInAction(() => {
          self.state = STATE_ERROR;
        });
        logError(
          {
            errorCode: 'FETCH_BUCKET_STATS',
            description: 'Error while attempting to fetchApplications',
          },
          {
            error,
            jobId,
            advertiserId: rootStore.seekAdvertiserId,
            sort: 'SCORE',
            sortOrder: 'ASC',
            pageSize: 0,
          },
        );
      }
    });

    return {
      loadBucketStats,
    };
  });

export interface IBucketStatsStore extends Instance<typeof BucketStatsStore> {}
export default BucketStatsStore;
